import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import * as facturacion from '@/api/facturacion.api';
import { EntityLink } from '@/components/datos/EntityLink';
import { Kpi } from '@/components/datos/ModuleHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dineroCent } from '@/lib/formato-tienda';

/**
 * IMPUESTOS — el mes ante la DGII, con las cuentas de FacturApp.
 *
 * Cuánto ITBIS se facturó, cuánto se cobró, cuánto retuvieron los clientes y
 * cuánto se pagó en gastos; y el 607 (ventas con comprobante) listo para bajar.
 *
 * Todo sale del servidor. Con lo fiscal apagado se ve en cero y lo dice: no
 * hay nada que declarar con un papel sin NCF.
 */

const mesDeHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function bajar(archivo: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  Object.assign(document.createElement('a'), { href: url, download: archivo }).click();
  URL.revokeObjectURL(url);
}

export function ImpuestosDgii() {
  const toast = useToast();
  const [periodo, setPeriodo] = useState(mesDeHoy);
  const valido = /^\d{4}-\d{2}$/.test(periodo);

  const { data: r, isLoading, isError, error } = useQuery({
    queryKey: ['facturacion', 'dgii', periodo],
    queryFn: () => facturacion.resumenDgii(periodo),
    enabled: valido,
  });

  const exportar = useMutation({
    mutationFn: () => facturacion.exportar607(periodo),
    onSuccess: ({ archivo, csv, filas }) => {
      bajar(archivo, csv);
      toast.success(`607 descargado: ${filas} comprobante${filas === 1 ? '' : 's'}.`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo generar el 607'),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {/* El campo ocupa todo el ancho de su caja: la caja es la que mide. */}
        <div className="w-48">
          <Input label="Mes" type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
        </div>
        <div className="text-right">
          <Button
            variant="outline"
            onClick={() => exportar.mutate()}
            disabled={!r || r.en607 === 0 || exportar.isPending}
          >
            <Download className="h-4 w-4" />
            Descargar 607 (ventas)
          </Button>
          <p className="mt-1 text-xs text-slate-500">
            {r && r.en607 === 0
              ? 'Este mes no hay ventas con comprobante fiscal.'
              : 'El 606 (compras) no está: el sistema no guarda el NCF de los suplidores.'}
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-600">{error instanceof Error ? error.message : 'Error'}</p>
      )}

      {r && (
        <>
          {r.itbisTasa === 0 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              El ITBIS y los comprobantes fiscales están apagados en este negocio: las facturas salen
              sin valor fiscal y aquí no hay nada que declarar.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi
              titulo={r.neto < 0 ? 'Crédito a favor' : 'A depositar a la DGII'}
              valor={dineroCent(Math.abs(r.neto))}
              pie="ITBIS cobrado − retenido − el de los gastos"
              tono={r.neto > 0 ? 'malo' : 'normal'}
            />
            <Kpi
              titulo="ITBIS facturado"
              valor={dineroCent(r.itbisFacturado)}
              pie={`Base ${dineroCent(r.montoFacturado)} · es lo que lleva el 607`}
            />
            <Kpi
              titulo="ITBIS cobrado"
              valor={dineroCent(r.itbisCobrado)}
              pie="De las facturas ya pagadas"
            />
            <Kpi
              titulo="ITBIS en gastos"
              valor={dineroCent(r.itbisEnGastos)}
              pie="Gastos pagados que llevan ITBIS"
            />
          </div>

          {(r.retItbis > 0 || r.retIsr > 0 || r.retencionesSinConstancia > 0) && (
            <div className="rounded-lg border border-surface-border p-3 text-sm text-slate-700 dark:text-slate-200">
              <p className="font-medium">Retenciones que hicieron los clientes</p>
              <p className="mt-1 text-slate-600 dark:text-slate-300">
                En lo ya cobrado: ITBIS {dineroCent(r.retItbis)} · ISR {dineroCent(r.retIsr)}
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Constancias de las facturas con retención del mes: {r.retencionesConConstancia}{' '}
                recibida{r.retencionesConConstancia === 1 ? '' : 's'} ·{' '}
                <span className={r.retencionesSinConstancia > 0 ? 'font-semibold text-red-600' : ''}>
                  {r.retencionesSinConstancia} por pedir
                </span>
              </p>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-surface-border">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Factura</th>
                  <th className="px-3 py-2 font-medium">Cliente</th>
                  <th className="px-3 py-2 font-medium">NCF</th>
                  <th className="px-3 py-2 text-right font-medium">Base</th>
                  <th className="px-3 py-2 text-right font-medium">ITBIS</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {r.facturas.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                      No hay facturas en este mes.
                    </td>
                  </tr>
                )}
                {r.facturas.map((f) => (
                  <tr key={f.id}>
                    <td className="px-3 py-2 tabular-nums text-slate-600 dark:text-slate-300">{f.fecha}</td>
                    <td className="px-3 py-2">
                      <EntityLink tipo="factura" id={f.id} etiqueta={f.numero} detalle={f.clienteNombre} mono sinIcono />
                    </td>
                    <td className="px-3 py-2 text-slate-900 dark:text-slate-100">
                      {f.clienteNombre}
                      {f.clienteDocumento && (
                        <span className="block font-mono text-xs text-slate-500">{f.clienteDocumento}</span>
                      )}
                      {f.retenido > 0 && (
                        <Badge variant={f.retConstancia ? 'success' : 'warning'} className="mt-0.5">
                          {f.retConstancia ? 'Retención con constancia' : 'Retención sin constancia'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {f.ncf ?? <span className="font-sans text-slate-400">sin NCF</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{dineroCent(f.montoCent)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dineroCent(f.itbisCent)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{dineroCent(f.brutoCent)}</td>
                  </tr>
                ))}
              </tbody>
              {r.facturas.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-surface-border font-semibold text-slate-900 dark:text-slate-100">
                    <td colSpan={4} className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">
                      Totales
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{dineroCent(r.montoFacturado)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dineroCent(r.itbisFacturado)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {dineroCent(r.montoFacturado + r.itbisFacturado)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {r.anuladasConNcf.length > 0 && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Anuladas con comprobante (se declaran aparte, en el 608):{' '}
              {r.anuladasConNcf.map((a) => `${a.ncf} (${a.numero})`).join(', ')}.
            </p>
          )}

          <p className="text-xs text-slate-500">
            «A depositar» toma el ITBIS de lo ya cobrado; el 607 lleva todo lo facturado. Cuál de las
            dos bases declara el negocio lo decide su contador.
          </p>
        </>
      )}
    </div>
  );
}
